import React, { useEffect, useRef, useState } from 'react';
import FormError from '../../../FormError';
import { apiFetch, errorText } from '../shared/api';
import Spinner from '../shared/Spinner';
import BusinessSearch from '../add/BusinessSearch';
import CollapsibleCardHeader from './CollapsibleCardHeader';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { formatPhoneAsTyped } from '../add/formatPhoneAsTyped';
import { formatPhoneUsDisplay } from '../add/formatPhoneUsDisplay';
import { toDateTimeLocal, fromDateTimeLocal } from './format';
import ChipList from './more/ChipList';
import { normalizeEmail, normalizePhone } from './more/normalizeChip';
import ContactsBlock from './more/ContactsBlock';

// A contact's phone is shown in US national form ((xxx) xxx-xxxx) but stored in
// E.164 (+1...) so SMS keeps a sendable number.
function phoneToE164(value) {
    const trimmed = (value || '').trim();
    if (trimmed === '') {
        return '';
    }
    const parsed = parsePhoneNumberFromString(trimmed, 'US');
    return parsed && parsed.isPossible() ? parsed.number : trimmed;
}

function contactRowFromApi(contact, timezone) {
    return {
        key: `contact-${contact.id}`,
        id: contact.id,
        first_name: contact.first_name || '',
        last_name: contact.last_name || '',
        phone: formatPhoneUsDisplay(contact.phone || ''),
        email: contact.email || '',
        reminder_date: toDateTimeLocal(contact.reminder_date, timezone),
        notes: contact.notes || '',
    };
}

function contactRowToPayload(row) {
    return {
        first_name: row.first_name,
        last_name: row.last_name,
        phone: phoneToE164(row.phone),
        email: row.email,
        reminder_date: fromDateTimeLocal(row.reminder_date),
        notes: row.notes,
    };
}

function valuesEqual(a, b) {
    if (Array.isArray(a) || Array.isArray(b)) {
        return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((value, index) => value === b[index]);
    }
    return a === b;
}

function snapshotFromDemo(demo) {
    const settings = demo.settings || {};

    return {
        use_departments: Boolean(settings.use_departments),
        is_international: Boolean(settings.is_international),
        default_country_code: settings.is_international ? (settings.default_country_code || null) : null,
        dark_mode: Boolean(demo.dark_mode),
        extra_emails_to_notify: settings.extra_emails_to_notify || [],
        extra_sms_to_notify: settings.extra_sms_to_notify || [],
        success_text_active: Boolean(settings.success_text_active),
        success_text: settings.success_text || null,
        success_link_text: settings.success_link_text || null,
        success_link_url: settings.success_link_url || null,
        google_place_id: demo.google_place_id || null,
        allow_gps_feedback: Boolean(settings.allow_gps_feedback),
        qr_code_include_logo: Boolean(settings.qr_code_include_logo),
        qr_code_logo_width: settings.qr_code_logo_width,
        is_disabled: Boolean(demo.is_disabled),
    };
}

function appendFormValue(formData, key, value) {
    if (typeof value === 'boolean') {
        formData.append(key, value ? '1' : '0');
    } else if (value === null || value === undefined) {
        formData.append(key, '');
    } else {
        formData.append(key, String(value));
    }
}

// DemoCompanyController::googlePlaceIdCheck() returns a bare `true`, a plain string, or
// (superusers only) a string with one <a href="..."> anchor — never render that raw HTML.
function parsePlaceCheckMessage(raw) {
    if (raw === true) {
        return { text: __('Available') };
    }
    if (typeof raw !== 'string') {
        return { text: String(raw) };
    }

    const decodeEntities = (value) => {
        const textarea = document.createElement('textarea');
        textarea.innerHTML = value;

        return textarea.value;
    };
    const stripTags = (value) => decodeEntities(value.replace(/<[^>]*>/g, ''));
    const match = raw.match(/<a\s+href="([^"]*)"[^>]*>(.*?)<\/a>/i);
    if (!match) {
        return { text: stripTags(raw) };
    }

    const [fullMatch, href, linkText] = match;

    return {
        before: stripTags(raw.slice(0, match.index)),
        href,
        linkText: stripTags(linkText),
        after: stripTags(raw.slice(match.index + fullMatch.length)),
    };
}

export default function MoreSettings({
    demo, endpoints, mapsApiKey, industries, countries, limits, cqrTypes, industryGps, onDemoChange, onToast,
    timezone = 'UTC', abilities = {}, onListingReplaced,
}) {
    const settings = demo.settings || {};

    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [collapsed, setCollapsed] = useState(true);

    const [useDepartments, setUseDepartments] = useState(Boolean(settings.use_departments));
    const [isInternational, setIsInternational] = useState(Boolean(settings.is_international));
    const [defaultCountryCode, setDefaultCountryCode] = useState(settings.default_country_code || '');
    const [darkMode, setDarkMode] = useState(Boolean(demo.dark_mode));
    const [extraEmails, setExtraEmails] = useState(settings.extra_emails_to_notify || []);
    const [extraSms, setExtraSms] = useState(settings.extra_sms_to_notify || []);
    const [successActive, setSuccessActive] = useState(Boolean(settings.success_text_active));
    const [successText, setSuccessText] = useState(settings.success_text || '');
    const [successLinkText, setSuccessLinkText] = useState(settings.success_link_text || '');
    const [successLinkUrl, setSuccessLinkUrl] = useState(settings.success_link_url || '');
    const [placeId, setPlaceId] = useState(demo.google_place_id || '');
    const [placeCheckMessage, setPlaceCheckMessage] = useState(null);
    const [showBusinessSearch, setShowBusinessSearch] = useState(false);
    const [allowGpsFeedback, setAllowGpsFeedback] = useState(Boolean(settings.allow_gps_feedback));
    const [faviconAction, setFaviconAction] = useState(null);
    const [faviconPreview, setFaviconPreview] = useState(null);
    const faviconInputRef = useRef(null);
    const faviconPreviewRef = useRef(null);
    const [contacts, setContacts] = useState(() => (demo.contacts || []).map((contact) => contactRowFromApi(contact, timezone)));
    const [qrIncludeLogo, setQrIncludeLogo] = useState(Boolean(settings.qr_code_include_logo));
    const [qrLogoWidth, setQrLogoWidth] = useState(settings.qr_code_logo_width);
    const [isDisabled, setIsDisabled] = useState(Boolean(demo.is_disabled));
    const [cqrCode, setCqrCode] = useState('');
    const [cqrResult, setCqrResult] = useState(null);
    const [cqrError, setCqrError] = useState(null);
    const [placeChecking, setPlaceChecking] = useState(false);
    const [cqrAssigning, setCqrAssigning] = useState(false);
    const [resendingMarketing, setResendingMarketing] = useState(false);

    const baselineRef = useRef(snapshotFromDemo(demo));
    const baselineContactsRef = useRef(contacts.map(contactRowToPayload));

    // The GPS default is owned by EditDemoAccount's industry select; this only
    // mirrors it in, never the other way around.
    useEffect(() => {
        setAllowGpsFeedback(industryGps.value);
    }, [industryGps.seq]);

    useEffect(() => () => {
        if (faviconPreviewRef.current) {
            URL.revokeObjectURL(faviconPreviewRef.current);
        }
    }, []);

    const contactsChanged = () => JSON.stringify(contacts.map(contactRowToPayload)) !== JSON.stringify(baselineContactsRef.current);

    const buildDiff = () => {
        const current = {
            use_departments: useDepartments,
            is_international: isInternational,
            default_country_code: isInternational ? (defaultCountryCode || null) : null,
            dark_mode: darkMode,
            extra_emails_to_notify: extraEmails,
            extra_sms_to_notify: extraSms,
            success_text_active: successActive,
            success_text: successText || null,
            success_link_text: successLinkText || null,
            success_link_url: successLinkUrl || null,
            google_place_id: placeId || null,
            allow_gps_feedback: allowGpsFeedback,
            qr_code_include_logo: qrIncludeLogo,
            qr_code_logo_width: qrLogoWidth,
            is_disabled: isDisabled,
        };

        const diff = {};
        Object.keys(current).forEach((key) => {
            if (!valuesEqual(current[key], baselineRef.current[key])) {
                diff[key] = current[key];
            }
        });

        if (faviconAction === 'remove') {
            diff.favicon = null;
        }

        if (contactsChanged()) {
            diff.contacts = contacts.map(contactRowToPayload);
        }

        return diff;
    };

    const applySavedDemo = (savedDemo, { clearFavicon = true } = {}) => {
        onDemoChange(savedDemo);
        baselineRef.current = snapshotFromDemo(savedDemo);
        baselineContactsRef.current = (savedDemo.contacts || [])
            .map((contact) => contactRowFromApi(contact, timezone))
            .map(contactRowToPayload);
        if (clearFavicon) {
            setFaviconAction(null);
        }
        setErrors({});
    };

    const handleSave = async () => {
        const diff = buildDiff();
        const faviconFile = faviconAction instanceof File ? faviconAction : null;

        if (Object.keys(diff).length === 0 && !faviconFile) {
            return;
        }

        setSubmitting(true);
        setErrors({});

        try {
            let savedDemo = null;

            // A single multipart request can't express an emptied array/object diff key: FormData
            // simply has no entry for it, so UpdateDemoCompanyRequest's `sometimes` rule reads the
            // field as untouched and silently keeps the old value. So a favicon upload is sent as two
            // requests — every non-file diff key as JSON first (where `[]`/`{}` are expressible), then
            // the file alone as multipart — instead of folding the diff into the multipart body.
            if (Object.keys(diff).length > 0) {
                const { status, json } = await apiFetch(endpoints.update, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(diff),
                });

                if (status === 422) {
                    setErrors(json?.errors || {});
                    onToast({ type: 'error', text: __('Please check the form and try again.') });
                    return;
                }
                if (status < 200 || status >= 300) {
                    onToast({ type: 'error', text: errorText(json, __('Something went wrong. Please try again.')) });
                    return;
                }
                savedDemo = json.data;
            }

            if (faviconFile) {
                const formData = new FormData();
                appendFormValue(formData, '_method', 'PATCH');
                formData.append('favicon', faviconFile);

                const { status, json } = await apiFetch(endpoints.update, { method: 'POST', body: formData });

                if (status < 200 || status >= 300) {
                    if (savedDemo) {
                        applySavedDemo(savedDemo, { clearFavicon: false });
                        onToast({ type: 'error', text: __('The settings were saved but the favicon could not be uploaded.') });
                    } else {
                        onToast({ type: 'error', text: __('Something went wrong. Please try again.') });
                    }
                    setErrors(status === 422 && json?.errors ? json.errors : { favicon: [__('The favicon could not be uploaded.')] });
                    return;
                }
                savedDemo = json.data;
            }

            applySavedDemo(savedDemo);
            onToast({ type: 'success', text: __('Settings saved') });
        } catch (error) {
            console.error(error);
            onToast({ type: 'error', text: __('Something went wrong. Please try again.') });
        } finally {
            setSubmitting(false);
        }
    };

    const handleCheckPlace = async () => {
        setPlaceCheckMessage(null);
        setPlaceChecking(true);
        try {
            const { status, json } = await apiFetch(endpoints.googlePlaceIdCheck, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({ google_place_id: placeId, tellmyco_id: String(demo.id) }),
            });
            if (status >= 200 && status < 300) {
                setPlaceCheckMessage(parsePlaceCheckMessage(json));
            } else {
                setPlaceCheckMessage({ text: __('Could not check the Google Place ID right now.') });
            }
        } catch (error) {
            console.error(error);
            setPlaceCheckMessage({ text: __('Could not check the Google Place ID right now.') });
        } finally {
            setPlaceChecking(false);
        }
    };

    const handleBusinessSelected = (details) => {
        setPlaceId(details?.place_id || '');
        setPlaceCheckMessage(null);
        setShowBusinessSearch(false);

        if (onListingReplaced) {
            onListingReplaced({
                google_place_id: details?.place_id || '',
                name: details?.name || '',
                address: details?.address || '',
                phone: details?.phone || '',
                url: details?.website || '',
            });
        }
    };

    const handleAssignCqr = async () => {
        setCqrError(null);
        setCqrAssigning(true);
        try {
            const url = endpoints.cqrAssign.replace('__CQR__', encodeURIComponent(cqrCode));
            const { status, json } = await apiFetch(url, { method: 'POST' });
            if (status >= 200 && status < 300) {
                setCqrResult(json);
                onToast({ type: 'success', text: __('QR code assigned') });
            } else {
                setCqrResult(null);
                const firstError = Object.values(json?.errors || {})[0]?.[0];
                setCqrError(firstError || __('Something went wrong. Please try again.'));
            }
        } catch (error) {
            console.error(error);
            setCqrResult(null);
            setCqrError(__('Something went wrong. Please try again.'));
        } finally {
            setCqrAssigning(false);
        }
    };

    const handleResendMarketing = async () => {
        setResendingMarketing(true);
        try {
            const { status, json } = await apiFetch(endpoints.resendEmail, { method: 'POST' });
            if (status >= 200 && status < 300) {
                onToast({ type: 'success', text: __('Marketing package sent') });
            } else {
                onToast({ type: 'error', text: errorText(json, __('Something went wrong. Please try again.')) });
            }
        } catch (error) {
            console.error(error);
            onToast({ type: 'error', text: __('Something went wrong. Please try again.') });
        } finally {
            setResendingMarketing(false);
        }
    };

    const downloads = demo.downloads || {};
    const cqrTypeLabel = (value) => (value ? (cqrTypes[value]?.label ?? __('N/A')) : __('N/A'));

    // The trash control only shows while there is a favicon to drop - a freshly
    // picked file, or the stored one before it has been marked for removal.
    const faviconRemovable = faviconAction instanceof File || (Boolean(settings.favicon_url) && faviconAction !== 'remove');

    const setFaviconPreviewFor = (file) => {
        if (faviconPreviewRef.current) {
            URL.revokeObjectURL(faviconPreviewRef.current);
            faviconPreviewRef.current = null;
        }
        if (file) {
            const url = URL.createObjectURL(file);
            faviconPreviewRef.current = url;
            setFaviconPreview(url);
        } else {
            setFaviconPreview(null);
        }
    };

    const handleFaviconFileChange = (event) => {
        setFaviconPreviewFor(event.target.files[0] || null);
        setFaviconAction(event.target.files[0] || null);
    };

    // Clearing the native input's value is required: it is uncontrolled, so
    // resetting state alone would leave the chosen filename showing in the field.
    const handleFaviconRemove = () => {
        setFaviconPreviewFor(null);
        if (faviconInputRef.current) {
            faviconInputRef.current.value = '';
        }
        setFaviconAction(faviconAction instanceof File ? null : 'remove');
    };

    // Same source of truth as handleSave's early-return, surfaced so the button
    // greys out until something in the section actually changed.
    const isDirty = Object.keys(buildDiff()).length > 0 || faviconAction instanceof File;

    return (
        <div className="card demo-edit-form" data-testid="demo-edit-more">
            <CollapsibleCardHeader
                title={__('More Settings')}
                collapsed={collapsed}
                onToggle={() => setCollapsed((prev) => !prev)}
                controlsId="demo-edit-more-body"
            />

            <div className={`card-body${collapsed ? ' d-none' : ''}`} id="demo-edit-more-body">
                    <div data-testid="demo-edit-more-departments">
                        <ul className="list-unstyled">
                            {(demo.departments || []).map((department) => (
                                <li key={department.id}>{department.name}</li>
                            ))}
                        </ul>
                        <div className="custom-control custom-checkbox">
                            <input
                                id="demo-edit-more-use-departments"
                                type="checkbox"
                                className="custom-control-input"
                                checked={useDepartments}
                                onChange={(event) => setUseDepartments(event.target.checked)}
                            />
                            <label className="custom-control-label" htmlFor="demo-edit-more-use-departments">
                                {__('Use departments')}
                            </label>
                        </div>
                    </div>

                    <div data-testid="demo-edit-more-international">
                        <div className="custom-control custom-checkbox">
                            <input
                                id="demo-edit-more-international-toggle"
                                type="checkbox"
                                className="custom-control-input"
                                checked={isInternational}
                                onChange={(event) => setIsInternational(event.target.checked)}
                            />
                            <label className="custom-control-label" htmlFor="demo-edit-more-international-toggle">
                                {__('International phone numbers')}
                            </label>
                        </div>
                        {isInternational &&
                            <div className="form-group">
                                <label htmlFor="demo-edit-more-default-country">{__('Default country')}</label>
                                <select
                                    id="demo-edit-more-default-country"
                                    className="form-control"
                                    required
                                    value={defaultCountryCode}
                                    onChange={(event) => setDefaultCountryCode(event.target.value)}
                                >
                                    <option value="">{__('Select a country')}</option>
                                    {countries.map((country) => (
                                        <option key={country.code} value={country.code}>{country.name}</option>
                                    ))}
                                </select>
                                <FormError error_message={errors.default_country_code?.[0]} />
                            </div>
                        }
                    </div>

                    <div className="custom-control custom-checkbox" data-testid="demo-edit-more-dark-mode">
                        <input
                            id="demo-edit-more-dark-mode"
                            type="checkbox"
                            className="custom-control-input"
                            checked={darkMode}
                            onChange={(event) => setDarkMode(event.target.checked)}
                        />
                        <label className="custom-control-label" htmlFor="demo-edit-more-dark-mode">
                            {__('Dark mode')}
                        </label>
                    </div>

                    <div data-testid="demo-edit-more-notify">
                        <ChipList
                            testId="demo-edit-more-notify-emails"
                            label={__('Extra emails to notify')}
                            hint={__('(add one by one)')}
                            values={extraEmails}
                            onChange={setExtraEmails}
                            errors={errors}
                            field="extra_emails_to_notify"
                            normalize={normalizeEmail}
                        />
                        <ChipList
                            testId="demo-edit-more-notify-phones"
                            label={__('Extra phone numbers to notify')}
                            hint={__('(add one by one)')}
                            values={extraSms}
                            onChange={setExtraSms}
                            errors={errors}
                            field="extra_sms_to_notify"
                            formatDraft={formatPhoneAsTyped}
                            normalize={normalizePhone}
                        />
                    </div>

                    <div data-testid="demo-edit-more-success">
                        <div className="custom-control custom-checkbox">
                            <input
                                id="demo-edit-more-success-active"
                                type="checkbox"
                                className="custom-control-input"
                                checked={successActive}
                                onChange={(event) => setSuccessActive(event.target.checked)}
                            />
                            <label className="custom-control-label" htmlFor="demo-edit-more-success-active">
                                {__('Custom success page')}
                            </label>
                        </div>
                        {successActive &&
                            <>
                                <div className="form-group">
                                    <label htmlFor="demo-edit-more-success-text">{__('Success text')}</label>
                                    <textarea
                                        id="demo-edit-more-success-text"
                                        className="form-control"
                                        maxLength={limits.success_text}
                                        value={successText}
                                        onChange={(event) => setSuccessText(event.target.value)}
                                    />
                                    <FormError error_message={errors.success_text?.[0]} />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="demo-edit-more-success-link-text">{__('Link text')}</label>
                                    <input
                                        id="demo-edit-more-success-link-text"
                                        type="text"
                                        className="form-control"
                                        maxLength={limits.success_link_text}
                                        value={successLinkText}
                                        onChange={(event) => setSuccessLinkText(event.target.value)}
                                    />
                                    <FormError error_message={errors.success_link_text?.[0]} />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="demo-edit-more-success-link-url">{__('Link URL')}</label>
                                    <input
                                        id="demo-edit-more-success-link-url"
                                        type="text"
                                        className="form-control"
                                        value={successLinkUrl}
                                        onChange={(event) => setSuccessLinkUrl(event.target.value)}
                                    />
                                    <FormError error_message={errors.success_link_url?.[0]} />
                                </div>
                                <div data-testid="demo-edit-more-success-preview" className="card">
                                    <div className="card-body">
                                        <p>{successText}</p>
                                        {successLinkUrl &&
                                            <a href={successLinkUrl}>{successLinkText}</a>
                                        }
                                    </div>
                                </div>
                            </>
                        }
                    </div>

                    {abilities.edit_place_id &&
                    <div data-testid="demo-edit-more-place">
                        <div className="form-group">
                            <label htmlFor="demo-edit-more-place-id">{__('Google Place ID')}</label>
                            <div className="demo-island-field-row">
                                <input
                                    id="demo-edit-more-place-id"
                                    type="text"
                                    className="form-control"
                                    value={placeId}
                                    onChange={(event) => { setPlaceId(event.target.value); setPlaceCheckMessage(null); }}
                                />
                                <span className={`demo-island-btn-guard${!placeId.trim() || placeChecking ? ' is-disabled' : ''}`}>
                                    <button type="button" className="btn btn-secondary" disabled={!placeId.trim() || placeChecking} onClick={handleCheckPlace}>
                                        {placeChecking && <Spinner label={__('Checking')} className="mr-2" />}
                                        {__('Check ID')}
                                    </button>
                                </span>
                            </div>
                            {placeCheckMessage &&
                                <span data-testid="demo-edit-more-place-check-message">
                                    {placeCheckMessage.text ??
                                        <>
                                            {placeCheckMessage.before}
                                            <a href={placeCheckMessage.href} target="_blank" rel="noopener">
                                                {placeCheckMessage.linkText}
                                            </a>
                                            {placeCheckMessage.after}
                                        </>
                                    }
                                </span>
                            }
                            <FormError error_message={errors.google_place_id?.[0]} />
                        </div>
                        {placeId ?
                            <a
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-outline-primary demo-edit-external-btn"
                                href={`http://search.google.com/local/writereview?placeid=${placeId}`}
                            >
                                {__('Open review link')}
                                <i className="fas fa-external-link-alt ml-2" aria-hidden="true" />
                            </a>
                            :
                            <div className="text-muted small">
                                {__('Without a Google listing the Reputation Scorecard and the Google review link are unavailable.')}
                            </div>
                        }
                        <div>
                            <div className="custom-control custom-checkbox demo-edit-replace-listing">
                                <input
                                    id="demo-edit-more-replace-listing"
                                    type="checkbox"
                                    className="custom-control-input"
                                    checked={showBusinessSearch}
                                    onChange={(event) => setShowBusinessSearch(event.target.checked)}
                                />
                                <label className="custom-control-label" htmlFor="demo-edit-more-replace-listing">
                                    {__('Replace listing')}
                                </label>
                            </div>
                            {showBusinessSearch &&
                                <BusinessSearch
                                    endpoints={{ details: endpoints.businessSearchDetails }}
                                    mapsApiKey={mapsApiKey}
                                    onSelect={handleBusinessSelected}
                                />
                            }
                        </div>
                    </div>
                    }

                    <div className="custom-control custom-checkbox" data-testid="demo-edit-more-gps">
                        <input
                            id="demo-edit-more-gps"
                            type="checkbox"
                            className="custom-control-input"
                            checked={allowGpsFeedback}
                            onChange={(event) => setAllowGpsFeedback(event.target.checked)}
                        />
                        <label className="custom-control-label" htmlFor="demo-edit-more-gps">
                            {__('Allow GPS feedback')}
                        </label>
                    </div>

                    <div data-testid="demo-edit-more-favicon" className="demo-add-logo-picker">
                        {faviconAction instanceof File && faviconPreview &&
                            <img src={faviconPreview} alt={__('Favicon preview')} className="demo-add-logo-preview" />
                        }
                        {!(faviconAction instanceof File) && settings.favicon_url && faviconAction !== 'remove' &&
                            <img src={settings.favicon_url} alt={__('Favicon')} className="demo-add-logo-preview" />
                        }

                        <div className="demo-add-logo-controls">
                            <button
                                type="button"
                                className="btn btn-outline-primary d-flex align-items-center justify-content-center"
                                onClick={() => window.open('https://favicongrabber.com/', '_blank', 'noopener')}
                            >
                                <i className="fas fa-search mr-2" aria-hidden="true" />
                                {__("Open Favicon Search for This Company's URL")}
                            </button>

                            <div className="demo-add-logo-actions">
                                <div className="custom-file">
                                    <input
                                        ref={faviconInputRef}
                                        type="file"
                                        accept="image/*"
                                        aria-label={__('Favicon')}
                                        className="demo-add-logo-file"
                                        onChange={handleFaviconFileChange}
                                    />
                                </div>

                                {faviconRemovable &&
                                    <button
                                        type="button"
                                        className="btn btn-link demo-add-logo-remove"
                                        onClick={handleFaviconRemove}
                                        title={__('Remove favicon')}
                                        aria-label={__('Remove favicon')}
                                    >
                                        <i className="fas fa-trash" aria-hidden="true" />
                                    </button>
                                }
                            </div>
                        </div>
                        <FormError error_message={errors.favicon?.[0]} />
                    </div>

                    <div data-testid="demo-edit-more-contacts">
                        <ContactsBlock contacts={contacts} onChange={setContacts} errors={errors} timezone={timezone} />
                    </div>

                    <div data-testid="demo-edit-more-qr">
                        <div className="custom-control custom-checkbox">
                            <input
                                id="demo-edit-more-qr-include-logo"
                                type="checkbox"
                                className="custom-control-input"
                                checked={qrIncludeLogo}
                                onChange={(event) => setQrIncludeLogo(event.target.checked)}
                            />
                            <label className="custom-control-label" htmlFor="demo-edit-more-qr-include-logo">
                                {__('Include logo in QR codes')}
                            </label>
                            <FormError error_message={errors.qr_code_include_logo?.[0]} />
                        </div>
                        <div className="form-group">
                            <label htmlFor="demo-edit-more-qr-width">{__('QR logo width')}</label>
                            <input
                                id="demo-edit-more-qr-width"
                                type="number"
                                className="form-control"
                                min="20"
                                max="1000"
                                value={qrLogoWidth}
                                onChange={(event) => setQrLogoWidth(Number(event.target.value))}
                            />
                            <FormError error_message={errors.qr_code_logo_width?.[0]} />
                        </div>
                    </div>

                    <div data-testid="demo-edit-more-cqr">
                        <div className="form-group">
                            <label htmlFor="demo-edit-more-cqr-code">{__('Assign physical QR code')}</label>
                            <div className="demo-island-field-row">
                                <input
                                    id="demo-edit-more-cqr-code"
                                    type="text"
                                    className="form-control"
                                    aria-label={__('Assign physical QR code')}
                                    value={cqrCode}
                                    onChange={(event) => setCqrCode(event.target.value)}
                                />
                                <span className={`demo-island-btn-guard${!cqrCode.trim() || cqrAssigning ? ' is-disabled' : ''}`}>
                                    <button type="button" className="btn btn-secondary" disabled={!cqrCode.trim() || cqrAssigning} onClick={handleAssignCqr}>
                                        {cqrAssigning && <Spinner label={__('Assigning')} className="mr-2" />}
                                        {__('Assign QR code')}
                                    </button>
                                </span>
                            </div>
                        </div>
                        <FormError error_message={cqrError} />
                        {cqrResult &&
                            <div data-testid="demo-edit-more-cqr-result" className="card">
                                <div className="card-body">
                                    <div>{cqrResult.business_group_name}</div>
                                    <div>{cqrResult.business_name}</div>
                                    <div>{cqrResult.custom_url}</div>
                                    <div>{cqrTypeLabel(cqrResult.type)}</div>
                                    <div>{cqrTypeLabel(cqrResult.initial_type)}</div>
                                </div>
                            </div>
                        }
                    </div>

                    <div className="custom-control custom-checkbox" data-testid="demo-edit-more-status">
                        <input
                            id="demo-edit-more-status"
                            type="checkbox"
                            className="custom-control-input"
                            checked={isDisabled}
                            onChange={(event) => setIsDisabled(event.target.checked)}
                        />
                        <label className="custom-control-label" htmlFor="demo-edit-more-status">
                            {__('Disabled')}
                        </label>
                    </div>

                    <div className="demo-edit-more-actions">
                        <div data-testid="demo-edit-more-marketing" className="demo-edit-more-actions-contents">
                            <button type="button" className="btn btn-secondary" disabled={resendingMarketing} onClick={handleResendMarketing}>
                                {resendingMarketing && <Spinner label={__('Sending')} className="mr-2" />}
                                {__('Resend marketing package')}
                            </button>
                        </div>

                        <div data-testid="demo-edit-more-downloads" className="demo-edit-more-actions-contents">
                            {downloads.cart_qr &&
                                <a className="btn btn-secondary" href={downloads.cart_qr}>{__('Cart QR code (PNG)')}</a>
                            }
                            {downloads.cart_qr_svg &&
                                <a className="btn btn-secondary" href={downloads.cart_qr_svg}>{__('Cart QR code (SVG)')}</a>
                            }
                            <a className="btn btn-secondary" href={endpoints.feedbacks}>{__('Feedbacks submitted')}</a>
                        </div>
                    </div>

                    <div className="demo-island-save-wrapper demo-island-save-left">
                        <button type="button" className="btn btn-primary" disabled={submitting || !isDirty} onClick={handleSave}>
                            {submitting && <Spinner label={__('Saving')} className="mr-2" />}
                            {__('Save Extra Settings')}
                        </button>
                    </div>
                </div>
        </div>
    );
}
